"use client";
import { useEffect, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { canUseStudentPortal } from "@/lib/studentPortal";
import { getTokenStudent, setPortalToken, clearTokenStudent } from "@/lib/portalTokens";
import LoginMethodTabs from "@/components/auth/LoginMethodTabs";
import { parseApiError } from "@/lib/parseApiError";
import { loginWithPassword } from "@/lib/loginApi";
import { useWechatShareCard } from "@/hooks/useWechatShareCard";

function StudentLoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();

  useWechatShareCard({
    title: "蓝狐爱学",
    description: "登录蓝狐爱学，开启你的数学学习之旅",
  });

  useEffect(() => {
    let cancelled = false;

    const clearStudentOnly = () => {
      clearTokenStudent();
      if (typeof window !== "undefined" && localStorage.getItem("loginFrom") === "student") {
        localStorage.removeItem("loginFrom");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userMobile");
      }
    };

    const goChat = () => {
      const returnUrl = searchParams?.get("returnUrl");
      if (returnUrl) {
        try {
          const decoded = decodeURIComponent(returnUrl);
          if (decoded.startsWith("/") && !decoded.startsWith("//")) {
            router.replace(decoded);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      router.replace("/student/home");
    };

    const run = async () => {
      const token = getTokenStudent();
      if (!token) {
        return;
      }

      try {
        const resp = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) {
          clearStudentOnly();
          return;
        }
        const me = (await resp.json()) as { roles?: string[]; is_frozen?: boolean };
        const roles = me.roles || [];

        if (me.is_frozen === true) {
          clearStudentOnly();
          router.replace("/frozen");
          return;
        }

        if (!canUseStudentPortal(roles)) {
          clearStudentOnly();
          return;
        }

        localStorage.removeItem("hasOrgRole");
      } catch {
        return;
      }

      if (!cancelled) goChat();
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [router, searchParams]);

  const completeLogin = (data: {
    access_token: string;
    user?: { roles?: string[]; mobile?: string };
  }) => {
    const roles = data.user?.roles || [];

    if (!canUseStudentPortal(roles)) {
      throw new Error("您的账号没有学生端权限（账号需包含 student 角色）");
    }

    setPortalToken("student", data.access_token);
    localStorage.setItem("loginFrom", "student");
    localStorage.setItem("userRole", "student");
    localStorage.removeItem("hasOrgRole");

    if (data.user?.mobile) {
      localStorage.setItem("userMobile", data.user.mobile);
    }

    const returnUrl = searchParams?.get("returnUrl");
    if (returnUrl) {
      try {
        const decodedUrl = decodeURIComponent(returnUrl);
        if (decodedUrl.startsWith("/") && !decodedUrl.startsWith("//")) {
          router.push(decodedUrl);
          return;
        }
      } catch {
        /* fall through */
      }
    }
    router.push("/student/home");
  };

  const handleSmsLogin = async (mobile: string, code: string) => {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/users/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mobile, verification_code: code, portal: "student" }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const { message } = parseApiError(errorData, "登录失败");
      throw new Error(message);
    }

    completeLogin(await response.json());
  };

  const handlePasswordLogin = async (mobile: string, password: string) => {
    const data = await loginWithPassword(mobile, password, "student");
    completeLogin(data);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 justify-center items-center px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">登录 蓝狐爱学</h1>
      <LoginMethodTabs
        accent="indigo"
        portal="student"
        forgotPasswordHref="/forgot-password"
        onSmsLogin={handleSmsLogin}
        onPasswordLogin={handlePasswordLogin}
      />
    </div>
  );
}

export default function StudentLoginPage() {
  return (
    <Suspense fallback={
      <div className="flex flex-col h-screen bg-gray-50 justify-center items-center">
        <p className="text-gray-600">加载中...</p>
      </div>
    }>
      <StudentLoginContent />
    </Suspense>
  );
}
